import { useState } from 'react';
import { format, subDays, subMonths, startOfDay, endOfDay } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { DateRange } from 'react-day-picker';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface DateRangePickerProps {
  value: { from: Date; to: Date };
  onChange: (range: { from: Date; to: Date }) => void;
  className?: string;
}

const presets = [
  { labelKey: 'dateRange.last7Days', days: 7 },
  { labelKey: 'dateRange.last30Days', days: 30 },
  { labelKey: 'dateRange.last90Days', days: 90 },
  { labelKey: 'dateRange.last6Months', months: 6 },
  { labelKey: 'dateRange.last12Months', months: 12 },
  { labelKey: 'dateRange.allTime', all: true },
] as const;

export function DateRangePicker({ value, onChange, className }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation('common');
  const dateLocale = i18n.language === 'tr' ? tr : enUS;

  const handlePreset = (preset: (typeof presets)[number]) => {
    const to = endOfDay(new Date());
    let from: Date;
    if ('all' in preset) {
      from = new Date(2020, 0, 1);
    } else if ('months' in preset) {
      from = startOfDay(subMonths(new Date(), preset.months));
    } else {
      from = startOfDay(subDays(new Date(), preset.days));
    }
    onChange({ from, to });
    setOpen(false);
  };

  const handleSelect = (range: DateRange | undefined) => {
    if (range?.from && range?.to) {
      onChange({ from: startOfDay(range.from), to: endOfDay(range.to) });
    } else if (range?.from) {
      onChange({ from: startOfDay(range.from), to: endOfDay(range.from) });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn('justify-start text-left font-normal', className)}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {format(value.from, 'MMM d, yyyy', { locale: dateLocale })} - {format(value.to, 'MMM d, yyyy', { locale: dateLocale })}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <div className="flex">
          <div className="border-r p-3 space-y-1">
            {presets.map((preset) => (
              <Button
                key={preset.labelKey}
                variant="ghost"
                size="sm"
                className="w-full justify-start text-xs"
                onClick={() => handlePreset(preset)}
              >
                {t(preset.labelKey)}
              </Button>
            ))}
          </div>
          <div>
            <Calendar
              mode="range"
              selected={{ from: value.from, to: value.to }}
              onSelect={handleSelect}
              numberOfMonths={2}
              defaultMonth={subMonths(new Date(), 1)}
              locale={dateLocale}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
